/**
 * Configurações do CRM — documento único `crmSettings/main`.
 *
 * Guarda o responsável padrão de novos leads e o contato impresso nas propostas.
 * Antes esses valores estavam chumbados no código (CrmPage / PropostaBuilder /
 * crmSiteIngest). Espelha o padrão de `agendaSettingsRepo` (single-doc + subscribe).
 */

import { MESSA_FS } from '../repos/messaFirestorePaths.js';
import { messaOpLog, messaFirestoreErrBrief } from '../lib/messaOperationalLog.js';

function docRef() {
  const { collection, docId } = MESSA_FS.crmSettings;
  const db = window._fbDb;
  if (!db || !window._fbDoc) return null;
  return window._fbDoc(db, collection, docId);
}

function strOrEmpty(v) {
  return typeof v === 'string' ? v.trim() : '';
}

/**
 * @param {Record<string, unknown>|null|undefined} raw
 * @returns {{ leadDefaultOwnerEmail?: string, propostaContact?: object }}
 */
export function parseCrmSettingsDoc(raw) {
  if (!raw || typeof raw !== 'object') return {};
  const out = {};
  if (typeof raw.leadDefaultOwnerEmail === 'string') {
    out.leadDefaultOwnerEmail = raw.leadDefaultOwnerEmail.trim();
  }
  /**
   * (4.9) Validade do convite de onboarding, em dias. Guardada aqui porque é regra de CRM, e
   * lida pelo servidor em `crmOnboardingInvite.js` — os dois lados usam a mesma faixa (1 a 180)
   * e o mesmo padrão (30). Valor fora da faixa não é corrigido em silêncio na leitura: quem
   * grava é que trava, para a tela não mostrar um número diferente do que o servidor aplicou.
   */
  if (Number.isFinite(Number(raw.conviteValidadeDias))) {
    out.conviteValidadeDias = Number(raw.conviteValidadeDias);
  }
  if (raw.propostaContact && typeof raw.propostaContact === 'object') {
    const c = raw.propostaContact;
    out.propostaContact = {
      name: strOrEmpty(c.name),
      role: strOrEmpty(c.role),
      whatsapp: strOrEmpty(c.whatsapp),
      email: strOrEmpty(c.email),
      site: strOrEmpty(c.site),
    };
  }
  return out;
}

/**
 * @param {{ leadDefaultOwnerEmail?: string, propostaContact?: object }} payload
 * @param {{ reason?: string }} [meta]
 */
export async function persistCrmSettingsMainDoc(payload, meta = {}) {
  const ref = docRef();
  if (!ref || !window._fbSetDoc) {
    throw new Error('Firebase indisponível para gravar crmSettings.');
  }
  const reason =
    typeof meta.reason === 'string' && meta.reason.trim() ? meta.reason.trim().slice(0, 120) : 'crm-settings';
  const body = { updatedAt: Date.now(), schemaVersion: 1 };
  // Só grava campo informado, para um patch parcial não zerar o outro.
  if (typeof payload?.leadDefaultOwnerEmail === 'string') {
    body.leadDefaultOwnerEmail = payload.leadDefaultOwnerEmail.trim();
  }
  if (Number.isFinite(Number(payload?.conviteValidadeDias))) {
    const n = Math.round(Number(payload.conviteValidadeDias));
    body.conviteValidadeDias = Math.min(180, Math.max(1, n));
  }
  if (payload?.propostaContact && typeof payload.propostaContact === 'object') {
    const c = payload.propostaContact;
    body.propostaContact = {
      name: strOrEmpty(c.name),
      role: strOrEmpty(c.role),
      whatsapp: strOrEmpty(c.whatsapp),
      email: strOrEmpty(c.email),
      site: strOrEmpty(c.site),
    };
  }
  await window._fbSetDoc(ref, body, { merge: true });
  messaOpLog('crm-settings', 'info', 'persistCrmSettingsMainDoc', { reason });
}

/**
 * @param {(data: ReturnType<typeof parseCrmSettingsDoc>) => void} onData
 * @param {(err: unknown) => void} [onError]
 * @returns {() => void}
 */
export function subscribeCrmSettingsMain(onData, onError) {
  const ref = docRef();
  if (!ref || !window._fbOnSnapshot) return () => {};
  try {
    return window._fbOnSnapshot(
      ref,
      snap => {
        try {
          onData(parseCrmSettingsDoc(snap.exists() ? snap.data() : null));
        } catch (e) {
          messaOpLog('crm-settings', 'warn', 'subscribeCrmSettingsMain handler', messaFirestoreErrBrief(e));
        }
      },
      err => {
        messaOpLog('crm-settings', 'error', 'subscribeCrmSettingsMain', messaFirestoreErrBrief(err));
        if (typeof onError === 'function') onError(err);
      },
    );
  } catch (e) {
    messaOpLog('crm-settings', 'error', 'subscribeCrmSettingsMain falhou a subscrever', messaFirestoreErrBrief(e));
    return () => {};
  }
}

export const crmSettingsRepo = {
  subscribeMain: subscribeCrmSettingsMain,
  persistMainDoc: persistCrmSettingsMainDoc,
  parseDoc: parseCrmSettingsDoc,
};
